// segmentResolver.js
//
// Pure logic to evaluate a ruleTree against user_profiles_marketing docs.
// Split into two layers:
//   1. matchProfile(profile, ruleTree) → boolean  (pure, no Firestore)
//   2. fetchAndResolve(db, ruleTree, opts)        → fetches + filters, returns matches
//
// The pure matcher is testable with synthetic profiles. The fetcher uses
// Firestore queries where possible and falls back to in-memory filtering for
// non-indexable ops (geo, array contains, OR groups).
//
// See MARKETING_CAMPAIGNS_PLAN.md §2 (segment builder), §3.2 (ruleTree shape).

import { getField, validateRule, FIELD_CATALOG } from './fieldCatalog.js';

// --- Reachability AND (always enforced — marketing can't disable) ---
// Resolver silently AND's this on every query. Per plan boundary rule.
const REACHABILITY_RULES = [
  { field: 'marketingOptIn', op: 'neq', value: false },
  { field: 'fcmToken',       op: 'exists' },
];

// --- Path resolver ---
const getByPath = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
};

// --- Single-rule matcher ---
const matchRule = (profile, rule) => {
  const v = getByPath(profile, rule.field);
  switch (rule.op) {
    case 'eq':       return v === rule.value;
    case 'neq':      return v !== rule.value;
    case 'in':       return Array.isArray(rule.value) && rule.value.includes(v);
    case 'notIn':    return Array.isArray(rule.value) && !rule.value.includes(v);
    case 'gt':       return v != null && v > rule.value;
    case 'gte':      return v != null && v >= rule.value;
    case 'lt':       return v != null && v < rule.value;
    case 'lte':      return v != null && v <= rule.value;
    case 'between':  return v != null && v >= rule.value[0] && v <= rule.value[1];
    case 'contains':
      if (Array.isArray(v)) return v.includes(rule.value);
      if (typeof v === 'string') return v.includes(rule.value);
      return false;
    case 'containsAny':
      if (!Array.isArray(rule.value) || rule.value.length === 0) return false;
      if (Array.isArray(v)) return rule.value.some(x => v.includes(x));
      return rule.value.includes(v);
    case 'notContains':
      if (Array.isArray(v)) return !v.includes(rule.value);
      if (typeof v === 'string') return !v.includes(rule.value);
      return true;
    case 'exists':
      if (Array.isArray(v)) return v.length > 0;
      return v !== null && v !== undefined && v !== '';
    case 'notExists':
      if (Array.isArray(v)) return v.length === 0;
      return v === null || v === undefined || v === '';
    case 'withinRadius': {
      if (!rule.value || typeof rule.value.lat !== 'number' || typeof rule.value.lng !== 'number') return false;
      const km = rule.value.km;
      // Array of points (e.g. allGeoPoints): match if ANY point is within radius.
      if (Array.isArray(v)) {
        return v.some(pt => pt && typeof pt.latitude === 'number' && typeof pt.longitude === 'number'
          && haversineKm(pt.latitude, pt.longitude, rule.value.lat, rule.value.lng) <= km);
      }
      if (v && typeof v.latitude === 'number' && typeof v.longitude === 'number') {
        return haversineKm(v.latitude, v.longitude, rule.value.lat, rule.value.lng) <= km;
      }
      return false;
    }
    default:
      console.warn('Unknown operator:', rule.op);
      return false;
  }
};

// --- Group matcher (recursive — supports nested AND/OR) ---
const matchGroup = (profile, group) => {
  if (!group || !Array.isArray(group.rules) || group.rules.length === 0) return true;
  const isAny = group.match === 'any';
  if (isAny) {
    return group.rules.some(r => (r.rules ? matchGroup(profile, r) : matchRule(profile, r)));
  }
  return group.rules.every(r => (r.rules ? matchGroup(profile, r) : matchRule(profile, r)));
};

// Public: does this profile match the ruleTree (with reachability enforced)?
export const matchProfile = (profile, ruleTree, { enforceReachability = true } = {}) => {
  if (!profile) return false;
  if (enforceReachability) {
    for (const r of REACHABILITY_RULES) {
      if (!matchRule(profile, r)) return false;
    }
  }
  return matchGroup(profile, ruleTree);
};

// Public: validate every rule in a ruleTree. Returns array of errors (empty = valid).
export const validateRuleTree = (ruleTree) => {
  const errors = [];
  const walk = (group, path = 'root') => {
    if (!group || typeof group !== 'object') {
      errors.push(`${path}: not an object`); return;
    }
    if (group.match && !['all', 'any'].includes(group.match)) {
      errors.push(`${path}: match must be "all" or "any"`);
    }
    if (!Array.isArray(group.rules)) {
      errors.push(`${path}: rules must be an array`); return;
    }
    group.rules.forEach((r, i) => {
      if (r.rules) return walk(r, `${path}.rules[${i}]`);
      const e = validateRule(r);
      if (e) errors.push(`${path}.rules[${i}]: ${e}`);
    });
  };
  walk(ruleTree);
  return errors;
};

// --- Query-planner: which rules can be pushed to Firestore vs filtered in memory ---
// Firestore composite-query limits:
//   - 1 inequality field per query
//   - no OR (use `in` / `array-contains-any` with up to 30 values, or split queries)
//   - no geo distance
// We push the simplest possible filter and post-filter the rest in memory.
const FIRESTORE_PUSHDOWN_OPS = new Set(['eq', 'in', 'gt', 'gte', 'lt', 'lte']);

const isPushdownable = (rule) => {
  if (!rule || rule.rules) return false; // nested groups → in-memory
  const f = getField(rule.field);
  if (!f) return false;
  if (!FIRESTORE_PUSHDOWN_OPS.has(rule.op)) return false;
  return true;
};

// Public: split a ruleTree's top-level AND group into (firestore filters) + (in-memory rules).
// For OR groups or nested groups, we do everything in memory.
export const planQuery = (ruleTree) => {
  const firestoreFilters = [];
  const inMemoryRules = [];
  const isTopLevelAnd = !ruleTree || ruleTree.match !== 'any';
  if (!isTopLevelAnd) {
    // OR at the top level — push nothing, filter all in memory
    return { firestoreFilters: [], inMemoryRules: [ruleTree], strategy: 'in-memory-all' };
  }
  for (const r of (ruleTree.rules || [])) {
    if (isPushdownable(r)) firestoreFilters.push(r);
    else inMemoryRules.push(r);
  }
  // Firestore allows only ONE inequality field per query — keep the first, push the rest to memory
  const inequalityOps = new Set(['gt', 'gte', 'lt', 'lte']);
  let usedInequalityField = null;
  const keptFirestoreFilters = [];
  for (const r of firestoreFilters) {
    if (inequalityOps.has(r.op)) {
      if (usedInequalityField && usedInequalityField !== r.field) {
        inMemoryRules.push(r);
        continue;
      }
      usedInequalityField = r.field;
    }
    keptFirestoreFilters.push(r);
  }
  return { firestoreFilters: keptFirestoreFilters, inMemoryRules, strategy: 'hybrid' };
};

// --- Fetcher: applies the plan against a Firestore instance ---
// `dbCollection` is a Firestore CollectionReference to `user_profiles_marketing`.
// `limit` caps the candidate-set scan (reach preview uses ~5000).
export const fetchAndResolve = async (dbCollection, ruleTree, { limit = 5000, enforceReachability = true } = {}) => {
  const errors = validateRuleTree(ruleTree);
  if (errors.length) throw new Error('Invalid ruleTree: ' + errors.join('; '));

  const plan = planQuery(ruleTree);

  // Build Firestore query.
  // IMPORTANT: reachability (marketingOptIn / fcmToken) is enforced IN MEMORY, not at
  // Firestore level. Reasons:
  //   1. Firestore allows only ONE inequality field per query — a Firestore `!= false`
  //      would block any user rule using gt/gte/lt/lte (the common case).
  //   2. `== true` would exclude docs where the field is missing (forward-compat risk).
  //   3. Reachability is cheap to filter post-fetch — only a handful of fields to check.
  // The buildUserMarketingProfile cron always writes marketingOptIn explicitly, so
  // the in-memory check is reliable for profiles built by us.
  let q = dbCollection;
  for (const r of plan.firestoreFilters) {
    q = q.where(r.field, opToFirestore(r.op), r.value);
  }
  q = q.limit(limit);

  const snap = await q.get();
  const candidates = snap.docs.map(d => ({ uid: d.id, ...d.data() }));

  // In-memory filter — apply reachability + remaining rules + groups
  const matches = candidates.filter(p => {
    if (enforceReachability) {
      if (p.marketingOptIn === false) return false;
      if (!p.fcmToken) return false;
    }
    for (const r of plan.inMemoryRules) {
      if (r.rules) {
        if (!matchGroup(p, r)) return false;
      } else {
        if (!matchRule(p, r)) return false;
      }
    }
    return true;
  });

  return {
    matches,
    candidateCount: candidates.length,
    matchedCount: matches.length,
    candidateCappedAt: limit,
    hitLimit: candidates.length >= limit,
    strategy: plan.strategy,
  };
};

// Walk a ruleTree and collect the location values the segment is targeting.
// Used by reach breakdowns to filter aggregation to what marketing actually
// picked — a rule of `allEmirates contains Dubai` should show "Dubai: N", not
// every emirate those users also happen to have saved.
//
// Only positive/inclusive operators contribute (eq/in/contains/containsAny);
// negative operators (neq/notIn/notContains) don't define a target set. OR
// groups are treated as union — a user matching either branch of an OR
// counted toward that branch's constraint value.
export const extractLocationConstraints = (ruleTree) => {
  const cities = new Set();
  const emirates = new Set();
  const walk = (group) => {
    if (!group || !Array.isArray(group.rules)) return;
    for (const r of group.rules) {
      if (r.rules) { walk(r); continue; }
      const isEmirate = r.field === 'emirate' || r.field === 'allEmirates';
      const isCity = r.field === 'city' || r.field === 'allCities';
      if (!isEmirate && !isCity) continue;
      const target = isEmirate ? emirates : cities;
      switch (r.op) {
        case 'eq':
        case 'contains':
          if (r.value != null && r.value !== '') target.add(r.value);
          break;
        case 'in':
        case 'containsAny':
          if (Array.isArray(r.value)) r.value.forEach(v => { if (v != null && v !== '') target.add(v); });
          break;
        default:
          break;
      }
    }
  };
  walk(ruleTree);
  return { cities, emirates };
};

// Reach preview — returns counts + light breakdowns (language, top cities, top emirates).
//
// Breakdown semantics depend on whether the segment has a location constraint:
//   • Rule targets specific emirates/cities → aggregate only over the values in
//     the constraint. `allEmirates contains Dubai` shows "Dubai: N" — reflects what
//     was actually targeted, not the other emirates users happen to have saved.
//   • No location constraint → aggregate over every saved place (`allCities`,
//     `allEmirates`). Gives marketing a picture of "where are the matched users?"
//
// Falls back to the primary field on legacy profiles (allCities/allEmirates empty).
export const previewReach = async (dbCollection, ruleTree, opts = {}) => {
  const r = await fetchAndResolve(dbCollection, ruleTree, opts);
  const constraints = extractLocationConstraints(ruleTree);
  const hasCityConstraint = constraints.cities.size > 0;
  const hasEmirateConstraint = constraints.emirates.size > 0;

  const byLanguage = {};
  const byCity = {};
  const byEmirate = {};
  for (const p of r.matches) {
    byLanguage[p.language || 'unknown'] = (byLanguage[p.language || 'unknown'] || 0) + 1;

    const cities = Array.isArray(p.allCities) && p.allCities.length > 0
      ? p.allCities
      : (p.city ? [p.city] : []);
    const filteredCities = hasCityConstraint
      ? cities.filter(c => constraints.cities.has(c))
      : cities;
    for (const c of (filteredCities.length ? filteredCities : ['unknown'])) {
      byCity[c] = (byCity[c] || 0) + 1;
    }

    const emirates = Array.isArray(p.allEmirates) && p.allEmirates.length > 0
      ? p.allEmirates
      : (p.emirate ? [p.emirate] : []);
    const filteredEmirates = hasEmirateConstraint
      ? emirates.filter(e => constraints.emirates.has(e))
      : emirates;
    for (const e of (filteredEmirates.length ? filteredEmirates : ['unknown'])) {
      byEmirate[e] = (byEmirate[e] || 0) + 1;
    }
  }
  const topCities = Object.entries(byCity)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([city, count]) => ({ city, count }));
  const topEmirates = Object.entries(byEmirate)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 7)
    .map(([emirate, count]) => ({ emirate, count }));
  return {
    matchedCount: r.matchedCount,
    candidateCount: r.candidateCount,
    candidateCappedAt: r.candidateCappedAt,
    hitLimit: r.hitLimit,
    strategy: r.strategy,
    byLanguage,
    topCities,
    topEmirates,
    // Flags let the UI label the panels correctly ("matches your rule" vs "any saved place").
    cityBreakdownConstrained: hasCityConstraint,
    emirateBreakdownConstrained: hasEmirateConstraint,
  };
};

// --- Internals ---
const opToFirestore = (op) => {
  switch (op) {
    case 'eq': return '==';
    case 'in': return 'in';
    case 'gt': return '>';
    case 'gte': return '>=';
    case 'lt': return '<';
    case 'lte': return '<=';
    default: throw new Error(`op ${op} cannot be pushed to Firestore`);
  }
};

const haversineKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // km
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

// Re-export field catalog count for UI display ("X queryable fields available")
export const FIELD_COUNT = FIELD_CATALOG.length;
